/**
 * ErrorBoundary — Catches unhandled JS errors in child components.
 * Shows a friendly error screen with a refresh button instead of a white screen.
 * Especially important for PWA standalone mode where there's no browser refresh button.
 */
import React from "react";
import { F, DEFAULT_ACCENT } from "../constants";
import { logCrashError } from "../utils/crashLogger";
import { localStore, sessionStore } from "../utils/safeStorage";
import { isStaleChunkMessage, isUrllessChunkMessage, CHUNK_URL_IN_MESSAGE } from "../utils/staleChunkPattern";

// The message shapes (webpack's, the native import() wordings, Safari's MIME
// error) live in staleChunkPattern.js, shared with crashLogger and lazyChunk.
const isChunkLoadError = (error) => isStaleChunkMessage(error?.message || "");

// Shared cooldown key with crashLogger's global handlers — one auto-reload per
// 30 s window across every chunk-error entry point so they can't loop.
const CHUNK_RELOAD_KEY = "sendly-chunk-reload";
const CHUNK_RELOAD_COOLDOWN_MS = 30000;
// Safety net: if a triggered recovery can't actually reload (its own sw-purge
// guard hit MAX_ATTEMPTS / cooldown in index.html), flip the branded loader
// back to the error screen after this long so the user is never stuck staring
// at a loader that will never finish. Counted from the END of any wait the
// recovery publishes (window.__sendlyRecoveryPendingUntil — it may wait for the
// service worker's revalidation for up to ~13 s, or ~26 s, after navigation),
// so the loader does not turn into the error screen while a reload is still
// coming. chunkDeadlines.test.js pins that order.
const RECOVER_WATCHDOG_MS = 10000;

// How long from now the recovery watchdog should run: RECOVER_WATCHDOG_MS, or
// that long after the recovery's published wait ends, whichever is later.
function recoverWatchdogDelay() {
  let until = null;
  try { until = typeof window !== "undefined" ? window.__sendlyRecoveryPendingUntil : null; } catch { /* no window */ }
  const now = Date.now();
  return (typeof until === "number" && until > 0)
    ? Math.max(RECOVER_WATCHDOG_MS, until - now + RECOVER_WATCHDOG_MS)
    : RECOVER_WATCHDOG_MS;
}
// How long the recovery waits for the crash write to land before tearing the
// page down. `logCrashError` persists the record to localStorage synchronously
// and only drops it from that queue once Firestore ACKS the write — so a
// recovery that reloads mid-flight leaves a SUCCESSFULLY WRITTEN crash sitting
// in the queue, and the next boot's `flushPendingCrashes()` re-sends it with
// its ORIGINAL timestamp. The result is two byte-identical rows, same second,
// in the digest (observed 2026-08-20, chunk 5511) — burning two slots of the
// 20/device/day cap for one crash. The in-memory dedup can't catch it: that
// lives in `logCrashError`, and the flush calls `writeCrash` directly.
// Ordering the two instead of racing them closes the common case; the digest's
// own dedup covers a write that outlives this grace.
const CRASH_WRITE_GRACE_MS = 1500;

// Read-only predicate: will this error auto-recover (→ show the calm branded
// loader) rather than surface the error screen? True only for an ONLINE
// chunk-load error outside the reload cooldown. Called by BOTH
// getDerivedStateFromError (to pick the first render) and componentDidCatch (to
// actually trigger the reload) so the two always agree — no one-frame flash of
// the scary screen before the loader. Never mutates sessionStorage.
function willAutoRecover(error) {
  if (!isChunkLoadError(error)) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  // Storage unavailable reads as null → assume we can still try to recover.
  const last = sessionStore.get(CHUNK_RELOAD_KEY);
  if (last && Date.now() - Number(last) <= CHUNK_RELOAD_COOLDOWN_MS) return false;
  return true;
}

// Is the boot script's recovery there? Absent in dev (no service worker) and in
// tests that do not install it.
const hasBootRecovery = () => typeof window !== "undefined" && typeof window.__sendlyChunkRecovery === "function";

// Recover from a stale-chunk failure. A plain reload re-hits the stale SW still
// serving the old index.html (old chunk hashes) and fails again — so prefer the
// recovery exposed by index.html, which picks its path by evidence (a plain
// reload onto the HTML the SW already cached, activating a waiting worker, or
// unregister SW + purge caches + cache-bust reload) and actually escapes the
// bad state. `cause: 'chunk'` is what allows it to look at that evidence. Falls
// back to a plain reload in dev / when the SW isn't present.
function recoverFromChunkError(error) {
  try {
    if (hasBootRecovery()) {
      // Pass the failed chunk URL so the recovery can rewrite that exact
      // poisoned HTTP-cache entry (`cache: 'reload'`) before reloading — a plain
      // reload just re-hits the poison. Chrome and Firefox put the URL in the
      // import() error, and lazyChunk keeps that text when it re-shapes the
      // message: "Loading chunk Stats failed. (Failed to fetch dynamically
      // imported module: https://…/Stats.hash.chunk.js)". Safari names no URL —
      // for those two shapes ask the recovery to heal every /static/js/
      // resource it knows (`healAll`) instead of nothing. lazyChunk's own
      // timeout message is neither: no URL, no heal, plain reload.
      const msg = error?.message || "";
      const m = msg.match(CHUNK_URL_IN_MESSAGE);
      if (!m && isUrllessChunkMessage(msg)) window.__sendlyChunkRecovery(undefined, { cause: "chunk", healAll: true });
      else window.__sendlyChunkRecovery(m ? m[0] : undefined, { cause: "chunk" });
      return;
    }
  } catch { /* fall through to plain reload */ }
  try { window.location.reload(); } catch { /* SSR / test env */ }
}

// Must be a class component — React has no hook equivalent for componentDidCatch
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      // Snapshot the network state at the moment the error fired. Decides
      // whether to auto-reload (online — chunk hash is stale) or to show
      // the offline-friendly "this view isn't cached" screen.
      wasOffline: false,
      // True while an online stale-chunk error is auto-recovering — render the
      // calm branded loader instead of the "Something went wrong" screen.
      recovering: false,
    };
    this._onOnline = null;
    this._recoverTimer = null;
    // Identity token for the in-flight "wait for the crash write, then reload"
    // step. The reload is no longer synchronous with the catch, so a resetKey
    // change or an unmount inside the grace window must be able to cancel it —
    // otherwise a back gesture off the error screen would still purge the SW
    // and reload a page the user had already navigated away from.
    this._pendingRecovery = null;
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      wasOffline: typeof navigator !== "undefined" && navigator.onLine === false,
      // Decide up front (read-only) so the very first render already shows the
      // branded loader — no one-frame flash of the error screen before
      // componentDidCatch runs. componentDidCatch uses the same predicate.
      recovering: willAutoRecover(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // Kept as a promise: the chunk-recovery branch below awaits it (capped) so
    // the write isn't racing its own reload. `logCrashError` never rejects.
    const logged = logCrashError(error, {
      source: "ErrorBoundary",
      componentStack: errorInfo?.componentStack || "",
    });
    // Chunk-load failures have two distinct causes that need different
    // recovery flows:
    //  • ONLINE  → stale `index.html` references a chunk that was renamed
    //              by a new deploy. Auto-reload once (rate-limited) so the
    //              user picks up the new bundle without manually refreshing.
    //              React.lazy catches these internally so the global
    //              `unhandledrejection` handler never sees them — only this
    //              boundary does.
    //  • OFFLINE → the chunk simply isn't in the SW cache (the user has
    //              never visited this lazy-loaded route while online).
    //              Reloading will produce the same error — instead we show
    //              an offline-specific screen and auto-recover when the
    //              network returns.
    if (isChunkLoadError(error)) {
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (offline) {
        // Hand it to the boot script's recovery at once, still offline: it
        // deletes nothing and reloads nothing while offline, and arms ONE
        // braked reload for the `online` event (the sendly-html-reload brake
        // lives there, and it works before React too). The boundary used to
        // add its own unbraked `online` → reload listener; two owners of that
        // reload is one too many. Only without the boot recovery (dev) does
        // the boundary listen itself.
        if (hasBootRecovery()) {
          recoverFromChunkError(error);
        } else if (typeof window !== "undefined" && !this._onOnline) {
          this._onOnline = () => { window.location.reload(); };
          window.addEventListener("online", this._onOnline);
        }
        return;
      }
      // Online: getDerivedStateFromError already decided (via willAutoRecover,
      // the same read) whether we auto-recover, so render() is already showing
      // the branded loader instead of the error screen. Now actually trigger
      // the reload, mark the shared cooldown so this can't loop, and arm a
      // watchdog: if the recovery can't reload (its own sw-purge guard maxed
      // out), fall back to the error screen instead of a perpetual loader.
      // When recovering is false (cooldown active) we skip straight to the
      // error screen with its Refresh button.
      if (this.state.recovering) {
        sessionStore.set(CHUNK_RELOAD_KEY, String(Date.now()));
        // Let the crash write land first — capped, because a dead Firestore
        // connection would otherwise hold the user on the loader indefinitely.
        // The user is already looking at the branded loader, so the delay costs
        // nothing visible, and the recovery watchdog below still has 8.5s.
        const token = {};
        this._pendingRecovery = token;
        Promise.race([
          logged,
          new Promise((resolve) => setTimeout(resolve, CRASH_WRITE_GRACE_MS)),
        ]).then(() => {
          if (this._pendingRecovery !== token) return;  // reset / unmounted meanwhile
          this._pendingRecovery = null;
          recoverFromChunkError(error);
        });
        if (typeof window !== "undefined") this.armRecoverWatchdog(recoverWatchdogDelay());
      }
    }
  }

  // The recovery may publish its wait only AFTER this timer was armed (it asks
  // the service worker for evidence first), so a timer that comes due while a
  // published wait is still running re-arms to that wait's end + the full
  // RECOVER_WATCHDOG_MS instead of flipping to the error screen mid-wait.
  armRecoverWatchdog(delayMs) {
    if (this._recoverTimer) clearTimeout(this._recoverTimer);
    this._recoverTimer = setTimeout(() => {
      this._recoverTimer = null;
      const next = recoverWatchdogDelay();
      if (next > RECOVER_WATCHDOG_MS) { this.armRecoverWatchdog(next); return; }
      this.setState({ recovering: false });
    }, delayMs);
  }

  componentWillUnmount() {
    if (this._onOnline && typeof window !== "undefined") {
      window.removeEventListener("online", this._onOnline);
      this._onOnline = null;
    }
    if (this._recoverTimer) { clearTimeout(this._recoverTimer); this._recoverTimer = null; }
    this._pendingRecovery = null;
  }

  // Reset error state when the resetKey prop changes (e.g. on route change)
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, wasOffline: false, recovering: false });
      if (this._onOnline && typeof window !== "undefined") {
        window.removeEventListener("online", this._onOnline);
        this._onOnline = null;
      }
      if (this._recoverTimer) { clearTimeout(this._recoverTimer); this._recoverTimer = null; }
      this._pendingRecovery = null;
    }
  }

  handleRefresh = () => {
    // For a stale-chunk error a plain reload just re-hits the stale SW and lands
    // back on "Something went wrong" — use the aggressive recovery so the button
    // actually escapes. Non-chunk errors get a normal reload.
    if (isChunkLoadError(this.state.error)) {
      recoverFromChunkError(this.state.error);
      return;
    }
    window.location.reload();
  };

  // While an online stale-chunk error auto-recovers (SW purge + cache-bust
  // reload), show the same calm branded loader the app boots with — not the
  // alarming "Something went wrong". Self-contained (own <style> + keyframes)
  // because React has already removed index.html's #boot-screen and its styles
  // by the time this renders. Mirrors the #boot-screen markup 1:1.
  renderRecovering() {
    const lang = (localStore.get("sendly-lang") || "hu").slice(0, 2);
    const label = lang === "en" ? "Loading…" : "Betöltés…";
    return (
      <div className="sendly-recover" role="status" aria-label="Sendly">
        <style>{`
          .sendly-recover{min-height:100vh;min-height:100dvh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:#fafafa}
          .sendly-recover .rec-logo{filter:drop-shadow(0 8px 24px rgba(55,111,139,0.22))}
          .sendly-recover .rec-s{animation:sendly-rec-s 1.8s ease-in-out infinite}
          @keyframes sendly-rec-s{0%{stroke-dashoffset:100}50%{stroke-dashoffset:0}100%{stroke-dashoffset:-100}}
          .sendly-recover .rec-name{font-family:'Space Grotesk',sans-serif;font-weight:600;font-size:14px;color:#376f8b;opacity:.9}
          @media (prefers-color-scheme:dark){.sendly-recover{background:#111}.sendly-recover .rec-name{color:#9fc3d4}}
          html[data-theme="dark"] .sendly-recover{background:#111}
          html[data-theme="dark"] .sendly-recover .rec-name{color:#9fc3d4}
          html[data-theme="light"] .sendly-recover{background:#fafafa}
          @media (prefers-reduced-motion:reduce){.sendly-recover .rec-s{animation:none;stroke-dashoffset:0}}
        `}</style>
        <div className="rec-logo">
          <svg width="96" height="96" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect width="128" height="128" rx="28" fill="#376f8b" />
            <path d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="rgba(255,255,255,0.25)" strokeWidth="7" strokeLinecap="round" fill="none" />
            <path className="rec-s" d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="white" strokeWidth="7" strokeLinecap="round" fill="none" pathLength={100} strokeDasharray="100" />
            <circle cx="76" cy="22" r="8" fill="#eab308" />
            <circle cx="50" cy="36" r="8" fill="#dc2626" />
            <circle cx="74" cy="58" r="8" fill="rgba(255,255,255,0.85)" />
            <circle cx="84" cy="78" r="8" fill="#3d8b37" />
            <circle cx="54" cy="98" r="8" fill="#eab308" />
          </svg>
        </div>
        <div className="rec-name">{label}</div>
      </div>
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.recovering) return this.renderRecovering();

    const offlineChunk = this.state.wasOffline && isChunkLoadError(this.state.error);

    // Inline styles — no theme context available here since we're above the app tree
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: 32,
        fontFamily: F, background: "#111", color: "#eee", textAlign: "center",
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{offlineChunk ? "📴" : "⚠️"}</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          {offlineChunk ? "Offline mód" : "Something went wrong"}
        </div>
        <div style={{ fontSize: 14, color: "#999", marginBottom: 24, maxWidth: 360, lineHeight: 1.5 }}>
          {offlineChunk
            ? "Ez a nézet nincs lementve az eszközödre. Lépj online — automatikusan újratöltjük az oldalt, amint visszajön a kapcsolat."
            : "An unexpected error occurred. Please refresh the page to continue."}
        </div>
        <button
          onClick={this.handleRefresh}
          style={{
            padding: "12px 32px", borderRadius: 12, border: "none",
            background: DEFAULT_ACCENT, color: "#fff", fontSize: 15,
            fontWeight: 700, fontFamily: F, cursor: "pointer",
          }}
        >
          ↻ {offlineChunk ? "Próbáld újra" : "Refresh"}
        </button>
        {import.meta.env.DEV && this.state.error && (
          <pre style={{
            marginTop: 24, padding: 16, background: "#1a1a1a", borderRadius: 8,
            fontSize: 12, color: "#f87171", textAlign: "left", maxWidth: "90vw",
            overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}>
            {this.state.error.toString()}
            {this.state.error.stack && "\n\n" + this.state.error.stack}
          </pre>
        )}
      </div>
    );
  }
}
